import { postDataAPI, getDataAPI } from './api'

export interface Message {
  role: 'user' | 'assistant'
  content: string
  timestamp?: string
}

export interface ChatResponse {
  success: boolean
  response: string
  conversationId?: string
  conversation?: Message[]
  error?: string
}

export interface PresetPrompt {
  title: string
  content: string
  category?: string
  order?: number
}

/**
 * Default static prompts for the chat (relevant to the new system)
 */
export const DEFAULT_PROMPTS: PresetPrompt[] = [
  { title: 'Show my upcoming schedule', content: 'Show my upcoming schedule' },
  { title: 'What are my current tasks?', content: 'What are my current tasks?' },
  { title: 'Create a new task', content: 'Create a new task' },
  { title: 'Show my recent shows', content: 'Show my recent shows' },
  { title: 'View my music releases', content: 'View my music releases' },
  { title: 'Financial overview', content: 'Financial overview' },
  { title: 'Team tasks and assignments', content: 'Team tasks and assignments' },
  { title: 'Career health check', content: 'Career health check' },
  { title: 'Upcoming tour dates', content: 'Upcoming tour dates' },
  { title: 'Help me plan content', content: 'Help me plan content' },
]

/**
 * Send a message to the AI chat API (matching legacy processQuestionForDizee)
 */
export const sendChatMessage = async (
  question: string,
  conversationHistory: Message[] = [],
  conversationId: string = '',
  images: any[] = [],
  specialistId?: string
): Promise<ChatResponse> => {
  try {
    // Convert message format to match backend expectations
    const formattedHistory = conversationHistory.map((msg) => ({
      type: msg.role,
      content: msg.content,
    }))

    // Get AI mode status from localStorage (default true)
    const aiModeEnabled = JSON.parse(localStorage.getItem('dizeeAIModeEnabled') ?? 'true')

    const payload: Record<string, unknown> = {
      prompt: question,
      conversation: formattedHistory,
      conversationId: conversationId.trim() !== '' ? conversationId : '',
      images: images.map(img => ({
        url: img.url,
        name: img.name,
        type: img.fileType
      })),
      aiModeEnabled: aiModeEnabled,
    }
    if (specialistId?.trim()) {
      payload.specialistId = specialistId.trim()
    }

    console.log('🚀 Sending chat message to API:', {
      prompt: question.substring(0, 100),
      conversationId: payload.conversationId,
      historyLength: formattedHistory.length,
      imagesCount: images.length,
    })

    const raw = await postDataAPI('user/chatWithAIForDizee', payload)

    console.log('✅ Chat API raw response:', JSON.stringify(raw).substring(0, 500))

    // Backend returns: { status, message, data: { response, conversation, conversationId }, error, success }
    // postDataAPI returns axios response.data, so `raw` is the full JSON body.
    // Extract the AI response from whichever nesting level it lives at.
    const aiResponse =
      raw?.data?.response ??
      raw?.response ??
      raw?.data?.data?.response ??
      null

    const returnedConversationId =
      raw?.data?.conversationId ??
      raw?.conversationId ??
      raw?.data?.data?.conversationId ??
      null

    const conversation =
      raw?.data?.conversation ??
      raw?.conversation ??
      raw?.data?.data?.conversation ??
      null

    if (aiResponse) {
      return {
        success: true,
        response: aiResponse,
        conversationId: returnedConversationId,
        conversation: conversation,
      }
    } else {
      console.error('❌ No AI response found in payload:', JSON.stringify(raw).substring(0, 1000))
      return {
        success: false,
        response: raw?.message || 'No response received from AI. Please try again.',
        error: 'No response field in API data',
      }
    }
  } catch (error: any) {
    console.error('❌ Chat API Error:', error?.response?.status, error?.response?.data || error.message)
    const status = error?.response?.status
    const backendMsg = error?.response?.data?.message
    let userMsg = 'Failed to connect to AI service. Please try again.'
    if (status === 401 || status === 403) userMsg = 'Authentication error. Please sign in again.'
    else if (status === 429) userMsg = backendMsg || 'AI usage limit reached. Please try again later.'
    else if (status === 500) userMsg = 'The AI service encountered an error. Please try again.'
    else if (backendMsg) userMsg = backendMsg

    return {
      success: false,
      response: userMsg,
      error: backendMsg || error.message || 'Unknown error',
    }
  }
}

/**
 * Get conversation history by ID — tries API first, then local cache
 */
export const getConversationById = async (conversationId: string) => {
  // Try API first (interceptor handles auth token)
  try {
    const response = await getDataAPI(`user/conversations/${conversationId}`)
    const data = response?.data || response
    const messages = data?.messages || data?.conversation || response?.messages || response?.conversation
    if (messages && Array.isArray(messages) && messages.length > 0) {
      return { success: true, messages }
    }
  } catch {
    /* fall through to local cache */
  }

  // Fallback: load from local cache
  const local = getLocalConversation(conversationId)
  if (local) {
    return { success: true, messages: local.messages }
  }

  return { success: false, messages: [] }
}

/**
 * Get user's conversation history — merges API + local cache
 */
export const getConversationHistory = async () => {
  let apiConvs: any[] = []

  // Try API (interceptor handles auth token)
  try {
    const response = await getDataAPI('user/conversations')
    const data = response?.data || response
    const convs = data?.conversations || data?.data?.conversations || response?.conversations
    if (Array.isArray(convs)) apiConvs = convs
  } catch {
    /* silent */
  }

  // Merge with local cache (local is the reliable source)
  const localConvs = getLocalConversations()

  // Build a map: API convos keyed by ID, then overlay local ones
  const map = new Map<string, any>()
  for (const c of apiConvs) {
    const id = c._id || c.conversationId
    if (id) map.set(id, c)
  }
  for (const c of localConvs) {
    // Local always wins (has latest data)
    map.set(c._id, { ...map.get(c._id), ...c })
  }

  const merged = Array.from(map.values()).sort(
    (a, b) => new Date(b.updatedAt || 0).getTime() - new Date(a.updatedAt || 0).getTime(),
  )

  return {
    success: true,
    conversations: merged,
  }
}

// ── Local conversation cache ─────────────────────────────────────────
const LOCAL_CONVS_KEY = 'dizee:chatConversations'
const LOCAL_CONV_PREFIX = 'dizee:conv:'

export interface LocalConversation {
  _id: string
  title: string
  updatedAt: string
  messages: Array<{ role: string; content: string }>
}

/** Save or update a conversation in local storage */
export function saveLocalConversation(
  id: string,
  title: string,
  messages: Array<{ role: string; content: string }>,
) {
  // Save full conversation (for loading later)
  const entry: LocalConversation = {
    _id: id,
    title,
    updatedAt: new Date().toISOString(),
    messages,
  }
  try {
    localStorage.setItem(LOCAL_CONV_PREFIX + id, JSON.stringify(entry))
  } catch {
    /* storage full — ignore */
  }

  // Update the summary list
  const list = getLocalConversations()
  const idx = list.findIndex((c) => c._id === id)
  const summary = { _id: id, title, updatedAt: entry.updatedAt }
  if (idx >= 0) {
    list[idx] = { ...list[idx], ...summary }
  } else {
    list.unshift(summary as any)
  }
  try {
    localStorage.setItem(LOCAL_CONVS_KEY, JSON.stringify(list.slice(0, 50)))
  } catch {
    /* ignore */
  }
}

/** Get the summary list of local conversations */
export function getLocalConversations(): LocalConversation[] {
  try {
    return JSON.parse(localStorage.getItem(LOCAL_CONVS_KEY) || '[]')
  } catch {
    return []
  }
}

/** Get a single conversation's full data from local cache */
export function getLocalConversation(id: string): LocalConversation | null {
  try {
    const raw = localStorage.getItem(LOCAL_CONV_PREFIX + id)
    return raw ? JSON.parse(raw) : null
  } catch {
    return null
  }
}
